import { safeJsonParse } from '@/lib/utils/transform'
import type { DirectorDraftState } from '@/stores/params'

interface DirectorGlobal {
  frame_rate?: number
  start?: number
  end?: number
  ratio?: string
  prompt?: string
}

interface DirectorSegment {
  start?: number
  length?: number
  prompt?: string
  image?: { url?: string }
  video?: { url?: string }
  isEndFrame?: boolean
}

interface DirectorAudioClip {
  url?: string
  start?: number
  length?: number
  trimStart?: number
  name?: string
}

const pickExtPayload = (obj: Record<string, unknown> | null | undefined): Record<string, unknown> | null => {
  if (!obj) return null
  const ext = obj.ext
  if (typeof ext === 'string' && ext.trim()) {
    return safeJsonParse<Record<string, unknown>>(ext)
  }
  if (ext && typeof ext === 'object') {
    return ext as Record<string, unknown>
  }
  return null
}

/**
 * 从任务 sceneParams（或原始 JSON 字符串）解析导演台 ext 数据。
 */
export const parseDirectorPayload = (
  sceneParams: { ext?: string } | Record<string, unknown> | null | undefined,
  rawSceneParamsJson?: string
): Record<string, unknown> | null => {
  const fromParsed = pickExtPayload(sceneParams as Record<string, unknown> | null | undefined)
  if (fromParsed) return fromParsed

  if (rawSceneParamsJson) {
    const raw = safeJsonParse<Record<string, unknown>>(rawSceneParamsJson)
    return pickExtPayload(raw)
  }

  return null
}

/**
 * 将已应用的导演台数据还原为 DirectorDraftState，供导演控制台重新打开时恢复时间轴。
 */
export const directorPayloadToDraftState = (
  payload: Record<string, unknown>,
  fallbackResolution?: string
): DirectorDraftState => {
  const global = payload.global as DirectorGlobal | undefined
  const segments = (payload.segments as DirectorSegment[] | undefined) ?? []
  const audio = (payload.audio as DirectorAudioClip[] | undefined) ?? []

  const fps = global?.frame_rate ?? 24
  const seconds = global?.end ?? 10
  const frames = Math.max(1, Math.round(seconds * fps))
  const resolution = (fallbackResolution || '720P').toUpperCase()
  const ratio = global?.ratio || '16:9'

  const units: Array<Record<string, unknown>> = []

  segments.forEach((seg, index) => {
    const id = `u${index + 1}`
    const start = seg.start ?? 0
    const end = start + (seg.length ?? 0)

    if (seg.image?.url) {
      units.push({
        id,
        start,
        end,
        type: 'image',
        image: seg.image.url,
        imageS3Url: seg.image.url,
        prompt: seg.prompt || '',
        ...(seg.isEndFrame ? { isEndFrame: true } : {})
      })
      return
    }

    if (seg.video?.url) {
      units.push({
        id,
        start,
        end,
        type: 'video',
        videoSrc: seg.video.url,
        videoS3Url: seg.video.url,
        prompt: seg.prompt || ''
      })
      return
    }

    if (seg.prompt) {
      units.push({
        id,
        start,
        end,
        type: 'text',
        text: seg.prompt,
        prompt: seg.prompt
      })
      return
    }

    units.push({ id, start, end, type: 'empty' })
  })

  const audioClips: Array<Record<string, unknown>> = []
  audio.forEach((clip, index) => {
    if (!clip.url) return
    const start = clip.start ?? 0
    const end = start + (clip.length ?? 0)
    audioClips.push({
      id: `a${index + 1}`,
      start,
      end,
      src: clip.url,
      s3Url: clip.url,
      name: clip.name,
      trimStart: clip.trimStart ?? 0,
      naturalDuration: clip.length
    })
  })

  const firstStoryUnit = units.find(unit => unit.type !== 'empty') ?? units[0]
  const firstUnitId = firstStoryUnit?.id ? String(firstStoryUnit.id) : 'u1'

  return {
    version: 1,
    params: { frames, seconds, fps, resolution, ratio },
    units,
    audioClips,
    selection: { type: 'story', id: firstUnitId },
    rulerMode: 's',
    unitSeq: Math.max(units.length, 1),
    audioSeq: audioClips.length
  }
}
